import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/App";
import { authAPI } from "@/lib/api";
import { toast } from "sonner";
import { Package, Truck, Plane, Loader2, Users } from "lucide-react";

export default function UserTypeSelection() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [userType, setUserType] = useState("");
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    phone: "",
    company_name: "",
    company_type: "logistics",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userType) {
      toast.error("Please select how you want to use Setuxpress");
      return;
    }
    
    if (!formData.phone) {
      toast.error("Please enter your phone number");
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        user_type: userType,
        phone: formData.phone,
      };
      
      if (userType === "partner") {
        if (!formData.company_name) {
          toast.error("Please enter your company name");
          setLoading(false);
          return;
        }
        payload.company_name = formData.company_name;
        payload.company_type = formData.company_type;
      }
      
      const response = await authAPI.setUserType(payload);
      updateUser(response.data);
      
      toast.success("Profile setup complete!");
      navigate(userType === "partner" ? "/partner" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/logo-new.png" alt="Setuxpress" className="h-12" />
            
          </div>
          <h1 className="text-2xl font-bold mb-2">Welcome, {user?.name}!</h1>
          <p className="text-muted-foreground">How do you want to use Setuxpress?</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User Type Selection */}
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setUserType("user")}
              className={`p-6 border-2 transition-all ${
                userType === "user" 
                  ? "border-accent bg-accent/5" 
                  : "border-border hover:border-accent/50"
              }`}
              data-testid="select-user-type"
            >
              <Package className="w-10 h-10 mx-auto mb-3 text-accent" />
              <p className="font-bold">Ship Cargo</p>
              <p className="text-sm text-muted-foreground">Book cargo space</p>
            </button>
            
            <button
              type="button"
              onClick={() => setUserType("partner")}
              className={`p-6 border-2 transition-all ${
                userType === "partner" 
                  ? "border-primary bg-primary/5" 
                  : "border-border hover:border-primary/50"
              }`}
              data-testid="select-partner-type"
            >
              <Truck className="w-10 h-10 mx-auto mb-3 text-primary" />
              <p className="font-bold">List Space</p>
              <p className="text-sm text-muted-foreground">Offer cargo space</p>
            </button>
          </div>

          {/* Phone Number (Required for all) */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+91 9876543210"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="rounded-none"
              data-testid="phone-input"
            />
          </div>

          {/* Partner-specific fields */}
          {userType === "partner" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  type="text"
                  placeholder="Your company name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  required
                  className="rounded-none"
                  data-testid="company-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company_type">Business Type</Label>
                <Select
                  value={formData.company_type}
                  onValueChange={(value) => setFormData({ ...formData, company_type: value })}
                >
                  <SelectTrigger className="rounded-none" data-testid="company-type-select">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="logistics">Logistics Company</SelectItem>
                    <SelectItem value="jet_owner">Private Jet Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <Button 
            type="submit" 
            className="w-full rounded-none btn-hover bg-accent text-accent-foreground hover:bg-accent/90"
            disabled={loading || !userType}
            data-testid="continue-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
          </Button>
        </form>
      </div>
    </div>
  );
}
